import {
  BreadcrumbHeading,
  TabContent,
  TabList,
  TabsProvider,
  Tab,
  DownloadIcon,
  DuplicateIcon,
  Export,
  Spinner,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { usePriceScheme } from 'src/hooks/usePriceScheme';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import PriceSchemePricingTab from 'src/components/PriceSchemePricingTab';
import { Fragment, useState } from 'react';
import PriceSchemeBandsRulesTab from 'src/components/PriceSchemeBandsRulesTab';
import { PriceSchemeChannelRulesTab } from 'src/components/PriceSchemeChannelRulesTab/PriceSchemeChannelRulesTab';
import { PriceSchemeOptionGroupOverrides } from 'src/components/PriceSchemeOptionGroupOverrides';
import { ExportPriceSchemeModal } from 'src/components/ExportPriceSchemeModal/ExportPriceSchemeModal';
import { useNotifications } from 'src/hooks/useNotifications/useNotifications';
import { NotificationType } from 'src/api/getNotifications/getNotifications';
import PriceSchemesModal from '../../components/PriceSchemesModal';
import { ICloseModalOptions } from '../../components/PriceSchemesModal/types';
import { PricingSchemeCategoriesTab } from 'src/components/PricingSchemeCategoriesTab';
import { useAuthWrapper } from '@weareopenr/authkit';
import styles from './PriceScheme.module.scss';
import { PriceSchemeRulesTab } from '../../components/PriceSchemeRulesTab/PriceSchemeRulesTab';

export const PriceScheme = () => {
  const { priceSchemeId } = useParams<{ priceSchemeId?: string }>();
  const navigate = useNavigate();
  const { data: priceScheme, isLoading } = usePriceScheme(priceSchemeId);
  const { featureFlags } = useAuthWrapper();
  const [isPriceSchemeExportModalOpen, setIsPriceSchemeExportModalOpen] = useState<boolean>(false);

  const { data: notifications = [] } = useNotifications();

  const hasPriceSchemeExportNotification = notifications.some(
    (notification) => notification.type === NotificationType.PRICE_SCHEME_EXPORT,
  );

  const toggleExportPriceSchemeModal = () => {
    setIsPriceSchemeExportModalOpen((prev) => !prev);
  };

  const [isPriceSchemeModalOpen, setIsPriceSchemeModalOpen] = useState<boolean>(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState<boolean>(false);

  const handlePriceSchemeModal = () => setIsPriceSchemeModalOpen((prev) => !prev);
  const handleDuplicateModal = () => setIsDuplicateModalOpen((prev) => !prev);
  const closePriceSchemeModal = (options?: ICloseModalOptions) => {
    setIsPriceSchemeModalOpen(false);
    setIsDuplicateModalOpen(false);
    if (options?.duplicated) navigate(Routes.PRICE_SCHEMES);
  };

  if (isLoading)
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );

  if (!priceSchemeId || !priceScheme) return <Navigate to={Routes.PRICE_SCHEMES} replace />;
  const newSchemeRuleTab = featureFlags.includes('pricing-rules');
  return (
    <TabsProvider defaultTab="pricing">
      <div className={styles['page-container']}>
        <PriceSchemesModal
          priceSchemeId={isPriceSchemeModalOpen ? priceSchemeId : undefined}
          duplicateSchemeId={isDuplicateModalOpen ? priceSchemeId : undefined}
          open={isPriceSchemeModalOpen || isDuplicateModalOpen}
          closeModal={closePriceSchemeModal}
        />
        <BreadcrumbHeading
          breadcrumbs={[
            { path: Routes.PRICE_SCHEMES, label: 'Price Schemes' },
            { path: `${Routes.PRICE_SCHEMES}/${priceScheme.id}`, label: priceScheme.name },
          ]}
          optionMenuItems={[
            {
              id: 'import-price-scheme',
              name: 'Import CSV',
              icon: DownloadIcon,
              onOptionClick: handlePriceSchemeModal,
            },
            {
              id: 'duplicate-price-scheme',
              name: 'Duplicate',
              icon: DuplicateIcon,
              onOptionClick: handleDuplicateModal,
            },
            {
              id: 'export-csv-template',
              name: 'Export Scheme as CSV',
              onOptionClick: toggleExportPriceSchemeModal,
              isDisabled: hasPriceSchemeExportNotification,
              icon: Export,
            },
          ]}
        />
        <ExportPriceSchemeModal
          isOpen={isPriceSchemeExportModalOpen}
          onClose={toggleExportPriceSchemeModal}
          priceSchemeId={priceSchemeId}
        />

        <TabList>
          <Tab id="pricing" tabText="Pricing" />
          {!newSchemeRuleTab && (
            <>
              <Tab id="bands-rules" tabText="Price Bands & Rules" />
              <Tab id="channel-rules" tabText="Channel Rules" />
            </>
          )}
          <Tab id="option-group-overrides" tabText="Option Group Overrides" />
          {featureFlags.includes('pricing_categories') && <Tab id="pricing-categories" tabText="Pricing Categories" />}
          {newSchemeRuleTab && <Tab id="pricing-rules" tabText="Pricing Rules" />}
        </TabList>

        <TabContent id="pricing">
          <Fragment key={priceSchemeId}>
            <PriceSchemePricingTab priceSchemeId={priceSchemeId} />
          </Fragment>
        </TabContent>
        <TabContent id="bands-rules">
          <PriceSchemeBandsRulesTab priceSchemeId={priceSchemeId} />
        </TabContent>

        <TabContent id="channel-rules">
          <PriceSchemeChannelRulesTab priceSchemeId={priceSchemeId} />
        </TabContent>

        {featureFlags.includes('pricing_categories') && (
          <TabContent id="pricing-categories">
            <Fragment key={priceSchemeId}>
              <PricingSchemeCategoriesTab priceSchemeId={priceSchemeId} />
            </Fragment>
          </TabContent>
        )}
        <TabContent id="option-group-overrides">
          <PriceSchemeOptionGroupOverrides priceSchemeId={priceSchemeId} />
        </TabContent>
        <TabContent id="pricing-rules">
          <PriceSchemeRulesTab priceSchemeId={priceSchemeId} />
        </TabContent>
      </div>
    </TabsProvider>
  );
};
